import { CheckCircleFilled } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Flex,
  Form,
  Input,
  Select,
  Tag,
  Typography,
  message,
} from 'antd';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { InputRef } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../../lib/api-client';
import { orDash } from '../../lib/format';
import { findUnitByChassis, listLocations, moveUnit, type Unit } from './api';

const { Text, Title } = Typography;

/**
 * The scanner screen (CLAUDE.md §10).
 *
 * Keyboard-wedge first: a barcode scanner is a keyboard that types fast and
 * presses Enter. So there is ONE always-focused input, it accepts a typed
 * chassis number identically, and the result lands on the same screen — no
 * navigation, no second page, nothing to dismiss.
 *
 * Used in a workshop, on a phone, with gloves on. Every target is large, the
 * result is readable at arm's length, and focus returns to the input the moment
 * anything completes so the next bike can be scanned without touching anything.
 */
export function ScanPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const inputRef = useRef<InputRef>(null);
  const [code, setCode] = useState('');
  const [unit, setUnit] = useState<Unit | null>(null);
  const [scannedWith, setScannedWith] = useState<'MANUAL' | 'BARCODE'>('MANUAL');
  const [toLocationUid, setToLocationUid] = useState<string | undefined>(undefined);
  const [reason, setReason] = useState('');
  const [notFound, setNotFound] = useState<string | null>(null);
  const [movedTo, setMovedTo] = useState<string | null>(null);

  // A wedge scanner types the whole code in a few milliseconds. If a human did
  // it, it took longer — which is how this tells them apart without a setting.
  const firstKeyAt = useRef<number | null>(null);

  const { data: locations } = useQuery({ queryKey: ['unit-locations'], queryFn: listLocations });

  const focusInput = (): void => {
    inputRef.current?.focus();
  };
  useEffect(focusInput, []);

  const lookup = useMutation({
    mutationFn: (chassis: string) => findUnitByChassis(chassis),
    onSuccess: (found) => {
      setUnit(found);
      setNotFound(null);
      setMovedTo(null);
      setToLocationUid(undefined);
      setReason('');
      setCode('');
      focusInput();
    },
    onError: (_error: unknown, chassis: string) => {
      setUnit(null);
      setMovedTo(null);
      // Not a toast: on a phone at arm's length a toast is gone before anybody
      // looks up. The screen itself says the code was not recognised.
      setNotFound(chassis);
      setCode('');
      focusInput();
    },
  });

  const move = useMutation({
    mutationFn: () =>
      moveUnit(unit?.uid ?? '', {
        toLocationUid: toLocationUid ?? '',
        reason: reason.trim().length > 0 ? reason.trim() : t('scan.defaultReason'),
        source: scannedWith,
      }),
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ['units'] });
      setUnit(updated);
      setMovedTo(updated.location);
      setToLocationUid(undefined);
      setReason('');
      // Straight back to the input: the next bike is already in their hand.
      focusInput();
    },
    onError: (e: unknown) => {
      void message.error(e instanceof ApiError ? e.message : t('scan.moveFailed'));
    },
  });

  const submit = (): void => {
    const value = code.trim();
    if (value.length === 0) return;
    const elapsed = firstKeyAt.current === null ? Infinity : Date.now() - firstKeyAt.current;
    setScannedWith(elapsed < 250 ? 'BARCODE' : 'MANUAL');
    firstKeyAt.current = null;
    lookup.mutate(value);
  };

  return (
    <Flex vertical gap={16}>
      <Card>
        <Form
          layout="vertical"
          onSubmitCapture={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <Form.Item label={<Title level={5}>{t('scan.prompt')}</Title>} style={{ marginBottom: 0 }}>
            <Input
              ref={inputRef}
              // 16px+ so iOS does not zoom the viewport on focus, which would
              // break the layout mid-scan.
              className="tabular"
              style={{ fontSize: 20, height: 56 }}
              size="large"
              autoFocus
              autoComplete="off"
              inputMode="text"
              value={code}
              placeholder={t('scan.placeholder')}
              onChange={(e) => {
                if (firstKeyAt.current === null) firstKeyAt.current = Date.now();
                setCode(e.target.value.toUpperCase());
              }}
              onBlur={() => {
                // Nothing else on this screen wants the keyboard. Losing focus
                // means the next scan types into nowhere.
                window.setTimeout(focusInput, 0);
              }}
              onPressEnter={submit}
            />
          </Form.Item>
        </Form>
      </Card>

      {movedTo !== null && (
        <Alert
          type="success"
          showIcon
          icon={<CheckCircleFilled />}
          message={t('scan.moved', { chassis: unit?.chassisNo ?? '', location: movedTo })}
        />
      )}

      {notFound !== null && (
        <Alert
          type="error"
          showIcon
          message={t('scan.notFound', { code: notFound })}
          description={t('scan.notFoundHelp')}
        />
      )}

      {unit === null && notFound === null && (
        <Card>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('scan.empty')} />
        </Card>
      )}

      {unit !== null && (
        <Card
          title={
            <Flex vertical gap={2}>
              <Title level={4} className="tabular" style={{ margin: 0 }}>
                {unit.chassisNo}
              </Title>
              <Text type="secondary">{orDash(unit.model)}</Text>
            </Flex>
          }
          extra={
            <Button
              onClick={() => {
                void navigate(`/units/${unit.uid}`);
              }}
            >
              {t('scan.openUnit')}
            </Button>
          }
        >
          <Flex vertical gap={16}>
            <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
              <Descriptions.Item label={t('vehicles.fields.status')}>
                <Tag>{t(`vehicles.status.${unit.stockStatus}`)}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('scan.currentLocation')}>
                <Text strong>{orDash(unit.location)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('vehicles.fields.colour')}>{orDash(unit.colour)}</Descriptions.Item>
              <Descriptions.Item label={t('scan.allocatedTo')}>
                {orDash(unit.allocatedCustomerName)}
              </Descriptions.Item>
            </Descriptions>

            <Form layout="vertical">
              <Form.Item label={t('scan.moveTo')}>
                <Select
                  size="large"
                  showSearch
                  value={toLocationUid}
                  onChange={setToLocationUid}
                  optionFilterProp="label"
                  placeholder={t('scan.moveToPlaceholder')}
                  options={(locations ?? [])
                    .filter((l) => l.name !== unit.location)
                    .map((l) => ({ value: l.uid, label: `${l.name} · ${l.code}` }))}
                />
              </Form.Item>
              <Form.Item label={t('scan.reason')} help={t('scan.reasonHelp')}>
                <Input
                  size="large"
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                  }}
                  placeholder={t('scan.defaultReason')}
                />
              </Form.Item>
              <Button
                type="primary"
                size="large"
                block
                style={{ height: 52 }}
                loading={move.isPending}
                disabled={toLocationUid === undefined}
                onClick={() => {
                  move.mutate();
                }}
              >
                {t('scan.moveIt')}
              </Button>
            </Form>
          </Flex>
        </Card>
      )}
    </Flex>
  );
}
